import { useState } from "react"
import { Formik } from "formik"
import * as Yup from "yup"
import { Paragraph } from "../../Components/Paragraph"
import { MultiStepFlowContainer } from "../../Components/MultiStepFlow"
import MentorFields from "../../Components/MentorFields"
import { useNavigate } from "react-router-dom"
import { useOutletContextPlanActions } from "../../hooks/useOutletContextPlanActions"
import Spinner from "../../Components/Spinner"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { InviteStatusBadge } from "../../Components/InviteStatusBadge"
import { PLAN_STATUS } from "../../constants/planStatus"
import LockedStep from "../../Components/LockedStep"
import { isStepLocked } from "../../lib/plan"
import Button from "../../Components/Button"
import { INVITE_STATUS } from "../../constants/inviteStatus"
import api from "../../services/api"

const addMentorSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email address").required("Email is required"),
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
})

/**
 * Component to add a new mentor
 */
const AddMentorForm = ({ token, onMentorAdded }) => {
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const handleSubmit = async (values, { resetForm }) => {
    setIsAdding(true)
    setError(null)
    try {
      await api.addMentors(token, [values])
      resetForm()
      setShowForm(false)
      onMentorAdded?.()
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add mentor")
    } finally {
      setIsAdding(false)
    }
  }

  if (!showForm) {
    return (
      <Button
        type="button"
        onClick={() => setShowForm(true)}
        bgColor="bg-circle hover:bg-circle/90"
        className="mt-4"
      >
        <i className="fa-solid fa-plus mr-2"></i>
        Add Another Mentor
      </Button>
    )
  }

  return (
    <Formik
      initialValues={{ email: "", first_name: "", last_name: "" }}
      validationSchema={addMentorSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, submitForm }) => (
        <div className="mt-6 w-full rounded-lg border border-gray-200 bg-gray-50 p-6">
          <div className="mb-4 text-center text-sm font-semibold text-gray-700">Add New Mentor</div>
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}
          <MentorFields />
          <div className="flex justify-center gap-2">
            <Button
              type="button"
              variant="gray-ghost"
              padding="py-2 px-4"
              onClick={() => setShowForm(false)}
              disabled={isAdding}
            >
              Cancel
            </Button>
            <Button
              type="button"
              bgColor="bg-circle hover:bg-circle/90"
              padding="py-2 px-4"
              onClick={submitForm}
              disabled={isAdding}
            >
              {isAdding ? (
                <div className="flex items-center gap-1">
                  <Spinner size="xs" />
                  <span>Adding...</span>
                </div>
              ) : (
                "Add Mentor"
              )}
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}

/**
 * Component to render dynamic mentor email fields after invites are sent
 */
const MentorEmailFieldsAfterInvitesSent = ({ mentors, token, onMentorUpdate }) => {
  const [loadingMentorId, setLoadingMentorId] = useState(null)
  const [activeAction, setActiveAction] = useState(null)
  const [error, setError] = useState(null)

  const handleSendInvite = async (mentor) => {
    setActiveAction("send-invite")
    setLoadingMentorId(mentor.id)
    setError(null)
    try {
      await api.inviteMentor(token, mentor.id)
      onMentorUpdate?.()
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send invite")
    } finally {
      setLoadingMentorId(null)
    }
  }

  const handleResendInvite = async (mentor) => {
    setActiveAction("resend-invite")
    setLoadingMentorId(mentor.id)
    setError(null)
    try {
      await api.inviteMentor(token, mentor.id)
      onMentorUpdate?.()
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend invite")
    } finally {
      setLoadingMentorId(null)
    }
  }

  const handleRemoveMentor = async (mentor) => {
    setActiveAction("remove-mentor")
    setLoadingMentorId(mentor.id)
    setError(null)
    try {
      await api.removeMentor(token, mentor.id)
      onMentorUpdate?.()
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove mentor")
    } finally {
      setLoadingMentorId(null)
    }
  }

  return (
    <div className="mt-6 w-full space-y-3">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}
      <ul className="space-y-3">
        {mentors?.map((mentor, index) => {
          const isLoading = loadingMentorId === mentor.id
          return (
            <li
              key={mentor.id || index}
              className="flex flex-col gap-5 rounded-lg border border-circle bg-white p-4 shadow-sm md:items-center md:justify-between"
            >
              <div className="grid w-full flex-grow grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                <span className="text-left text-lg font-bold text-gray-900">
                  {mentor.first_name} {mentor.last_name}
                </span>
                <span className="block text-left text-sm text-gray-600 sm:hidden">
                  {mentor.email}
                </span>
                <InviteStatusBadge status={mentor.invitation_status} className={"mx-0 sm:mx-auto"} />
                <span className="hidden text-left text-sm text-gray-600 sm:block md:text-right">
                  {mentor.email}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(mentor.invitation_status === INVITE_STATUS.PENDING ||
                  mentor.invitation_status === INVITE_STATUS.CANCELLED) && (
                  <Button
                    bgColor="bg-circle hover:bg-circle/90"
                    padding="py-2 px-4"
                    type="button"
                    onClick={() => handleResendInvite(mentor)}
                    disabled={isLoading}
                  >
                    {isLoading && activeAction === "resend-invite" ? (
                      <div className="flex items-center gap-1">
                        <Spinner size="xs" />
                        <span>Sending...</span>
                      </div>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane mr-1"></i>
                        Resend
                      </>
                    )}
                  </Button>
                )}
                {mentor.invitation_status === INVITE_STATUS.NOT_INVITED && (
                  <Button
                    bgColor="bg-circle hover:bg-circle/90"
                    padding="py-2 px-4"
                    type="button"
                    onClick={() => handleSendInvite(mentor)}
                    disabled={isLoading}
                  >
                    {isLoading && activeAction === "send-invite" ? (
                      <div className="flex items-center gap-1">
                        <Spinner size="xs" />
                        <span>Sending...</span>
                      </div>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane mr-1"></i>
                        Invite
                      </>
                    )}
                  </Button>
                )}

                {(mentor.invitation_status === INVITE_STATUS.NOT_INVITED ||
                  mentor.invitation_status === INVITE_STATUS.PENDING) && (
                  <Button
                    variant="gray-ghost"
                    padding="py-2 px-4"
                    type="button"
                    onClick={() => handleRemoveMentor(mentor)}
                    disabled={isLoading}
                  >
                    {isLoading && activeAction === "remove-mentor" ? "Removing..." : "Remove"}
                  </Button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * InviteMentors
 *
 * Multi-step flow for inviting mentors to join the mentorship circle.
 * This is step 4 of the setup checklist.
 */
const InviteMentors = () => {
  const { loading } = useOutletContextPlanActions()
  const { mentors, currentStep, plan, getPlan, studentFirstName } = useOutletContextPlan()
  const navigate = useNavigate()

  const handleMentorUpdate = () => {
    getPlan()
  }

  const mentorsInvitedSteps = [
    {
      id: "mentors-invited",
      encouragement: "✅",
      encouragementClassName: "text-3xl",
      title: "Great! You've invited Mentors!",
      renderCustomContent: () => {
        return (
          <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-4 text-center">
            <Paragraph>Below are the list of Mentors invited to support {studentFirstName} in this plan.</Paragraph>

            <div className="w-full">
              <MentorEmailFieldsAfterInvitesSent
                mentors={mentors}
                token={plan?.token}
                onMentorUpdate={handleMentorUpdate}
              />
            </div>

            <div className="w-full border-t pt-4">
              <AddMentorForm token={plan?.token} onMentorAdded={handleMentorUpdate} />
            </div>
          </div>
        )
      },
    },
  ]

  if (isStepLocked(PLAN_STATUS.SEND_PLAN, currentStep)) {
    return <LockedStep />
  }

  if (!plan) {
    return null
  }

  return (
    <>
      {loading && <Spinner overlay message="Inviting Mentors..." />}
      <MultiStepFlowContainer
        steps={mentorsInvitedSteps}
        stepId="student-invite-sent"
        fieldWidth="w-64 mx-auto"
        initialValues={{}}
        onBack={() => {
          navigate(`/plan/${plan?.token}/checklist`)
          return true
        }}
        alwaysShowBackButton={true}
        fieldContainerClassName="flex flex-col items-center"
        showProgress={false}
        onComplete={() => navigate("../")}
        completeButtonText="Back to Checklist"
        completeButtonProps={{
          width: "w-64",
          beforeIcon: (
            <i className="fa-solid fa-angle-left absolute left-4 top-1/2 -translate-y-1/2 text-3xl"></i>
          ),
        }}
      />
    </>
  )
}

export default InviteMentors
